import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useToast } from '../hooks/useToast'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useDataAdmission } from '../contexts/DataAdmissionContext'
import { useRunningJobs } from '../hooks/useRunningJobs'
import { useAutoRefreshOnCompletion } from '../hooks/useAutoRefreshOnCompletion'
import { useTranslate } from '../hooks/useTranslate'
import { DataSource as DataSourceType, Folder, FileColumn } from '../types'
import { HistoricalReportResponse } from '../types/historical'
import { useFolders } from '../hooks/useFolders'
import { dataObjectService } from '../services/dataObjectService'
import { asyncDataDiagnosisService } from '../services/asyncDataDiagnosisService'
import { authenticatedHistoricalReportService } from '../services/historicalReportService'
import FileUploadModal from '../components/FileUploadModal'
import AddDataSourceModal from '../components/AddDataSourceModal'
import HeaderSection from '../components/HeaderSection'
import MainView from '../components/MainView'
import FolderView from '../components/FolderView'
import CreateFolderModal from '../components/CreateFolderModal'
import DataObjectActionsModal from '../components/DataObjectActionsModal'
import MoveDataObjectModal from '../components/MoveDataObjectModal'
import FeedbackModal from '../components/FeedbackModal'
import PageLayout from '../components/layout/PageLayout'
import { getButtonClasses, getButtonStyles } from '../constants'
import { VscFeedback } from 'react-icons/vsc'
import { IoLanguage } from 'react-icons/io5'
import LanguageSelectorModal from '../components/LanguageSelectorModal'
import WelcomeBanner from '../components/training/WelcomeBanner'

const DataSource = () => {
    const USE_BACKEND = import.meta.env.VITE_USE_BACKEND === 'true';

    // Hooks must come first
    const { push: pushToast, clear: clearToasts } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const { activeLanguage } = useLanguage();
    const { t } = useTranslate();

    // Shared bootstrap data from context (single fetch, no duplication)
    const {
        dataSources, setDataSources,
        folders: preloadedFolders, setFolders: setPreloadedFolders,
        rootFolderUUID,
        isFirstLogin,
        loading, isInitialLoad, error,
        refreshData, silentRefresh,
    } = useDataAdmission();

    const [isModalOpen, setIsModalOpen] = useState(false)

    // Hook for managing running/pending jobs with selective polling
    const {
        runningJobs,
        getJobForDataObject,
        addWatchedObject,
        removeWatchedObject,
        getPersistedProgressForObject,
        recordActivity
    } = useRunningJobs({
        autoRefresh: true,
        refreshInterval: 8000, // Reduced frequency - now 8 seconds
        isAuthenticated: !!user?.email
    });

    // Track previous job count for auto-refresh detection
    const previousJobCountRef = useRef<number>(0);


    // Use folders hook; inject preloaded folders if present to skip internal fetch
    const { folders, createFolder, deleteFolder } = useFolders(dataSources, preloadedFolders)

    const [modalMode, setModalMode] = useState<'add' | 'analyse' | 'replace'>('add')
    const [selectedDataObject, setSelectedDataObject] = useState<DataSourceType | undefined>(undefined)
    const [showCreateFolder, setShowCreateFolder] = useState(false)
    const [pendingUploadFolder, setPendingUploadFolder] = useState<Folder | null>(null)
    // Track add flow state via ref only (avoid unused state warning)
    const isInAddDataObjectFlowRef = useRef(false)
    const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null)
    const [isInFolderView, setIsInFolderView] = useState(false)

    // Initialize folder view from URL parameters
    useEffect(() => {
        const folderIdParam = searchParams.get('folderId');
        if (folderIdParam && preloadedFolders.length > 0) {
            const folder = preloadedFolders.find(f => f.id === parseInt(folderIdParam));
            if (folder && (!selectedFolder || selectedFolder.id !== folder.id)) {
                setSelectedFolder(folder);
                setIsInFolderView(true);
            }
        } else if (!folderIdParam && isInFolderView) {
            // Clear folder view if no folderId in URL
            setSelectedFolder(null);
            setIsInFolderView(false);
        }
    }, [searchParams, preloadedFolders, selectedFolder, isInFolderView]);
    const [showAddDataSourceModal, setShowAddDataSourceModal] = useState(false)
    const [buttonDisabled, setButtonDisabled] = useState(false)
    const [showActionsModal, setShowActionsModal] = useState(false)
    const [actionsDataObject, setActionsDataObject] = useState<DataSourceType | undefined>(undefined)
    // Track which modal launched the current FileUploadModal so we can restore it on close
    const [previousModal, setPreviousModal] = useState<null | 'actions' | 'addDataSource' | 'createFolder'>(null)
    const [showMoveModal, setShowMoveModal] = useState(false)
    const [moveTarget, setMoveTarget] = useState<DataSourceType | undefined>(undefined)
    // Multi-select functionality
    const [isSelecting, setIsSelecting] = useState(false)
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [deleteTarget, setDeleteTarget] = useState<{ ids: number[], names: string[] } | null>(null)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [showFeedbackModal, setShowFeedbackModal] = useState(false)
    const [showLanguageModal, setShowLanguageModal] = useState(false)
    const [historicalReports, setHistoricalReports] = useState<HistoricalReportResponse[]>([])

    // Fetch historical reports for feedback modal (so users can tag reports)
    useEffect(() => {
        if (!user?.email || !USE_BACKEND) return;
        authenticatedHistoricalReportService.listReports({ status: 'analyzed', limit: 100 })
            .then(response => {
                if (response?.reports) {
                    setHistoricalReports(response.reports);
                }
            })
            .catch(() => {
                // Non-critical: feedback modal will work without report tagging
            });
    }, [user?.email, USE_BACKEND]);

    // Reset folder state when navigating to datasources route (e.g., via header home button)
    useEffect(() => {
        // Location changed - processing navigation

        // Only reset folder state when explicitly navigating from header home button
        // Check if navigation came from header (indicated by replace: true state)
        if (location.pathname === '/datasources' && !location.search && location.state?.fromHeader) {
            setIsInFolderView(false);
            setSelectedFolder(null);
        }
    }, [location.pathname, location.search, location.state]);

    // Extract bootstrap logic into reusable function for refreshing data
    // (Now delegates to context — refreshData shows loading, silentRefresh does not)

    // Fast refresh function that reloads data without loading screen
    const handleFastRefresh = useCallback(async () => {
        try {
            await silentRefresh();
        } catch (error) {
            console.error('Fast refresh failed:', error);
            window.location.reload();
        }
    }, [silentRefresh]);

    // Update data source statuses in real-time based on running jobs
    useEffect(() => {
        const currentJobCount = runningJobs.length;
        const previousJobCount = previousJobCountRef.current;

        // Running jobs updated - silent tracking

        // Auto-refresh when jobs complete (count decreases)
        if (previousJobCount > 0 && currentJobCount < previousJobCount) {

            // Trigger fast refresh to update data silently
            handleFastRefresh();
        }

        // Update ref for next comparison
        previousJobCountRef.current = currentJobCount;

        // Update data sources with job statuses - includes persisted progress
        setDataSources(prev => prev.map(ds => {
            const matchingJob = getJobForDataObject(ds.uuid || ds.id.toString());
            const persistedProgress = getPersistedProgressForObject(ds.uuid || ds.id.toString());

            if (matchingJob) {
                // Active job found - updating status
                if (matchingJob.status === 'RUNNING' && ds.status !== 'processing') {
                    addWatchedObject(ds.uuid || ds.id.toString()); // Watch this object
                    return { ...ds, status: 'processing' as const };
                } else if (matchingJob.status === 'SUCCESS' && ds.status !== 'processed') {
                    removeWatchedObject(ds.uuid || ds.id.toString()); // Stop watching
                    return { ...ds, status: 'processed' as const };
                } else if (matchingJob.status === 'FAILED' && ds.status !== 'error') {
                    removeWatchedObject(ds.uuid || ds.id.toString()); // Stop watching
                    return { ...ds, status: 'error' as const };
                }
            } else if (persistedProgress && ['RUNNING', 'PENDING'].includes(persistedProgress.status)) {
                // No active job but has persisted progress - restore watching if needed
                if (ds.status === 'processed' && persistedProgress.progress < 100) {
                    addWatchedObject(ds.uuid || ds.id.toString());
                    return { ...ds, status: 'processing' as const };
                }
            }

            return ds; // Keep existing status if no definitive change needed
        }));
    }, [runningJobs, getJobForDataObject]);

    // Refresh data object status - check if processing is complete
    const handleRefreshDataObject = async (dataSource: DataSourceType) => {
        try {
            // Find the running job for this data object
            const matchingJob = getJobForDataObject(dataSource.uuid || dataSource.id.toString());

            if (matchingJob) {
                // Get current job status
                const jobStatus = await asyncDataDiagnosisService.getJobStatus(matchingJob.id);

                // Check if job is complete (100% progress or SUCCESS status)
                if (jobStatus.status === 'SUCCESS' || jobStatus.progress >= 100) {
                    // Update data source status to processed
                    setDataSources(prev => prev.map(ds =>
                        ds.id === dataSource.id ? { ...ds, status: 'processed' as const } : ds
                    ));

                    pushToast({
                        variant: 'success',
                        message: `${dataSource.name} analysis completed! Report is now available.`
                    });
                } else if (jobStatus.status === 'FAILED') {
                    // Update data source status to error
                    setDataSources(prev => prev.map(ds =>
                        ds.id === dataSource.id ? { ...ds, status: 'error' as const } : ds
                    ));

                    pushToast({
                        variant: 'error',
                        message: `${dataSource.name} analysis failed. Please try re-analyzing.`
                    });
                } else {
                    // Still in progress, show current progress
                    pushToast({
                        variant: 'info',
                        message: `${dataSource.name} analysis in progress: ${jobStatus.progress.toFixed(0)}% complete`
                    });
                }
            } else {
                // No running job found - but don't assume completion, could be timing issue
                // Only show refresh info toast, don't change status
                pushToast({
                    variant: 'info',
                    message: `Checking ${dataSource.name} status... If analysis is complete, page will refresh automatically.`
                });
            }
        } catch (error) {
            console.error('[DataSource] Failed to refresh data object status:', error);
            pushToast({
                variant: 'error',
                message: 'Failed to refresh status. Please try again.'
            });
        }
    };

    // Remove debug observers – keep component lean

    // Bootstrap is handled by DataAdmissionContext — no local fetch needed

    // Hook for auto-refreshing data when any analysis completes
    useAutoRefreshOnCompletion(handleFastRefresh);

    const handleAddDataSource = () => {
        if (buttonDisabled) return;

        setButtonDisabled(true);
        setTimeout(() => setButtonDisabled(false), 200); // Prevent rapid clicking

        // In main view, open unified AddDataSourceModal (includes Databricks tab)
        if (!isInFolderView) {
            setShowAddDataSourceModal(true)
            return
        }
        // Fallback (folder view path should use handleAddDataObjectToFolder instead)
        setModalMode('add')
        setSelectedDataObject(undefined)
        setPreviousModal(null)
        setIsModalOpen(true)
    }

    const handleDataObjectClick = (dataObject: DataSourceType) => {
        setActionsDataObject(dataObject)
        setShowActionsModal(true)
    }

    // Multi-select functions
    const toggleSelection = () => {
        setIsSelecting(!isSelecting)
        setSelectedIds([]) // Clear selection when toggling
    }

    const handleCardSelection = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        }
    }

    const selectAllVisible = () => {
        const visibleIds = isInFolderView && selectedFolder
            ? dataSources.filter(ds => ds.folderId === selectedFolder.id).map(ds => ds.id)
            : unassignedDataSources.map(ds => ds.id)

        // Toggle: if all visible items are selected, deselect all; otherwise select all
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id))
        if (allSelected) {
            setSelectedIds([])
        } else {
            setSelectedIds(visibleIds)
        }
    }

    const clearSelection = () => {
        setSelectedIds([])
        setIsSelecting(false)
    }

    // Bulk operations
    const handleBulkMove = async (targetFolderId: number | undefined, createdFolder?: Folder) => {
        if (selectedIds.length === 0) return

        try {


            // Use created folder if provided, otherwise find in folders state
            let targetFolder = createdFolder || (targetFolderId ? folders.find(f => f.id === targetFolderId) : null)
            // When moving to root (unassign), use real root folder UUID
            const folderUuid = targetFolder?.uuid || (!targetFolderId ? rootFolderUUID : undefined)



            // Move all selected items
            await Promise.all(selectedIds.map(async id => {
                const dataObject = dataSources.find(ds => ds.id === id)

                if (dataObject && dataObject.uuid) {
                    await dataObjectService.updateDataSource(id, {
                        folderId: targetFolderId,
                        folderUuid: folderUuid,
                        dataObjectUuid: dataObject.uuid
                    })
                }
            }))

            // Update local state
            setDataSources(prev => prev.map(ds =>
                selectedIds.includes(ds.id) ? { ...ds, folderId: targetFolderId } : ds
            ))

            const destination = targetFolder ? targetFolder.name : 'main workspace'
            pushToast({
                variant: 'success',
                message: `Moved ${selectedIds.length} item(s) to ${destination}`
            })

            // Clear selection
            clearSelection()
        } catch (error) {
            console.error('Bulk move error:', error);
            pushToast({
                variant: 'error',
                message: `Failed to move items. Please try again.`
            })
        }
    }

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return

        // Prepare delete confirmation data
        const itemsToDelete = dataSources.filter(ds => selectedIds.includes(ds.id))
        const names = itemsToDelete.map(ds => ds.name)

        setDeleteTarget({ ids: selectedIds, names })
        setShowDeleteConfirmation(true)
    }

    const executeDelete = async () => {
        if (!deleteTarget) return

        try {
            await Promise.all(deleteTarget.ids.map(async id => {
                const dataObject = dataSources.find(ds => ds.id === id)
                if (dataObject && dataObject.uuid) {
                    await dataObjectService.deleteDataSource(dataObject.uuid)
                }
            }))

            // Update local state
            setDataSources(prev => prev.filter(ds => !deleteTarget.ids.includes(ds.id)))

            pushToast({
                variant: 'success',
                message: `Deleted ${deleteTarget.ids.length} item(s)`
            })

            clearSelection()
        } catch (error) {
            pushToast({
                variant: 'error',
                message: `Failed to delete items. Please try again.`
            })
        } finally {
            setShowDeleteConfirmation(false)
            setDeleteTarget(null)
        }
    }

    const handleCreateFolder = async (name: string) => {
        const result = await createFolder(name)
        if (!result.valid) {
            // Map validation errors object to first message if any
            const message = Object.keys(result.errors).length ? 'Folder name invalid' : 'Failed to create folder'
            pushToast({ variant: 'error', message })
        } else if (result.folder) {
            pushToast({ variant: 'success', message: `Created folder ${result.folder.name}` })
        }
        return result
    }

    const handleDeleteDataSource = async (id: number) => {
        try {
            const target = dataSources.find(d => d.id === id)
            if (!target) throw new Error('Data object not found')
            await dataObjectService.deleteDataSource(target.uuid || id)
            setDataSources(prev => prev.filter(ds => ds.id !== id))
            pushToast({ variant: 'success', message: `Deleted ${target.name}` })
        } catch {
            pushToast({ variant: 'error', message: 'Failed to delete data object' })
        }
    }

    const handleFolderClick = (folder: Folder) => {
        // Clear selection state when navigating to a folder
        setIsSelecting(false)
        setSelectedIds([])
        setSelectedFolder(folder)
        setIsInFolderView(true)
        // Update URL to preserve folder view
        setSearchParams({ folderId: folder.id.toString() });
    }

    const handleShowCreateFolder = () => {
        if (buttonDisabled) return;

        setButtonDisabled(true);
        setTimeout(() => setButtonDisabled(false), 200);

        setShowCreateFolder(true);
    };

    const handleAddDataObjectToFolder = () => {
        if (buttonDisabled) return;

        setButtonDisabled(true);
        setTimeout(() => setButtonDisabled(false), 200);

        // Set pending upload folder to current selected folder to preserve context
        if (selectedFolder) {
            setPendingUploadFolder(selectedFolder);
        }

        setShowAddDataSourceModal(true)
    }

    const handleAddNewDataObjectInFolder = () => {
        // When "Add New" is clicked from AddDataSourceModal in folder view
        if (selectedFolder) {
            setPendingUploadFolder(selectedFolder)
        }
        setModalMode('add')
        setPreviousModal('addDataSource')
        setIsModalOpen(true)
        setShowAddDataSourceModal(false)
    }
    const handleAddNewDataObjectFromModal = () => {
        setModalMode('add')
        setSelectedDataObject(undefined)
        setPreviousModal('addDataSource')
        setShowAddDataSourceModal(false)
        setIsModalOpen(true)
    }

    // Memoize unassigned data sources to prevent unnecessary re-computations
    const unassignedDataSources = useMemo(() =>
        dataSources.filter(ds => ds.folderId === undefined),
        [dataSources]
    )

    // Memoize filtered data objects to prevent infinite re-renders
    const filteredDataObjects = useMemo(() => {
        if (isInFolderView && selectedFolder) {
            return dataSources.filter(ds => ds.folderId === selectedFolder.id)
        }
        return unassignedDataSources
    }, [isInFolderView, selectedFolder, dataSources, unassignedDataSources])

    // Auto-exit selection mode if no selectable items remain
    useEffect(() => {
        if (isSelecting) {
            const selectableCount = isInFolderView ? filteredDataObjects.length : dataSources.length;
            if (selectableCount === 0) {
                setIsSelecting(false);
                setSelectedIds([]);
            }
        }
    }, [isSelecting, isInFolderView, filteredDataObjects.length, dataSources.length]);

    const assignDataSourcesToFolder = async (folderId: number, ids: number[], folderUuid?: string) => {
        try {
            // Use provided folderUuid if available, otherwise find from local folders state
            let targetFolderUuid = folderUuid
            if (!targetFolderUuid) {
                const targetFolder = folders.find(f => f.id === folderId)
                if (!targetFolder) throw new Error('Folder not found and no UUID provided')
                targetFolderUuid = targetFolder.uuid
            }

            await Promise.all(ids.map(async id => {
                const dataSource = dataSources.find(ds => ds.id === id)
                if (dataSource && dataSource.uuid) {
                    await dataObjectService.updateDataSource(id, {
                        folderId,
                        folderUuid: targetFolderUuid,
                        dataObjectUuid: dataSource.uuid
                    } as any)
                }
            }))
            setDataSources(prev => prev.map(ds => ids.includes(ds.id) ? { ...ds, folderId } : ds))
            pushToast({ variant: 'success', message: `Moved ${ids.length} item(s) to folder` })
        } catch {
            pushToast({ variant: 'error', message: 'Failed to move items to folder' })
        }
    }



    const handleCreateAndAddNewDataObject = (folder: Folder) => {
        flushSync(() => {
            setPendingUploadFolder(folder)
            isInAddDataObjectFlowRef.current = true
        })
        // Use AddDataSourceModal to show both upload and Databricks options
        setShowAddDataSourceModal(true)
        setShowCreateFolder(false)
    }

    const handleBackToMainView = () => {
        // Clear selection state when navigating back to main view
        setIsSelecting(false)
        setSelectedIds([])
        setIsInFolderView(false)
        setSelectedFolder(null)
        // Clear URL parameter when returning to main view
        setSearchParams({});
    }

    // Enhanced file upload with automatic analysis and report redirect
    const handleFileUploadWithAnalysis = async (file: File, dataObjectData?: { name: string; description?: string; columns?: any[] }) => {
        try {
            clearToasts(); // Clear any existing toasts before starting
            // Determine target folder based on current view / pending creation
            const targetFolder = pendingUploadFolder || (isInFolderView ? selectedFolder : undefined)

            // Note: Initial upload toast removed to avoid duplicates - final status toast shows result

            const result = await dataObjectService.createDataSourceWithAnalysis(
                file,
                dataObjectData?.columns || [],
                targetFolder || undefined, // Pass folder object or undefined
                dataObjectData?.description,
                activeLanguage
            );

            const finalObj = dataObjectData ? { ...result.dataSource, name: dataObjectData.name } : result.dataSource;
            setDataSources(prev => [...prev, finalObj]);

            // Handle folder view switching
            if (pendingUploadFolder && !isInFolderView) {
                setSelectedFolder(pendingUploadFolder);
                setIsInFolderView(true);
            }
            setPendingUploadFolder(null);

            // Note: Upload success toast removed to avoid duplicates - analysis progress shows status

            if (result.analysisReady && result.jobId) {
                pushToast({
                    variant: 'info',
                    message: `${finalObj.name} uploaded and analysis started. View progress in data object details.`
                });

                // Return jobId info so the modal can start progress tracking
                // Don't close modal yet - let progress popup manage modal closure
                return { jobId: result.jobId, dataObjectName: finalObj.name, dataObjectUuid: finalObj.uuid };
            } else {
                // If analysis job failed to start
                pushToast({
                    variant: 'warning',
                    message: `Upload completed but analysis could not be started. Please try the analyze button later.`
                });
                setIsModalOpen(false);
            }
        } catch (err) {
            console.error('Upload with analysis failed', err);
            // Show warning instead of error - file might have uploaded but analysis failed
            pushToast({
                variant: 'warning',
                message: 'Upload completed but analysis encountered an issue. Please try uploading again or contact support if the problem persists.'
            });
        }
    }

    // (Reserved) analyze-only handler removed for now; reintroduce when a dedicated UI action exists.

    // Analyze new uploaded file against existing data object with async progress tracking
    // Flow: analyzeReplacedFile internally calls async analysis -> open actions modal for progress
    const handleFileReplaceWithAnalysis = async (file: File, dataObjectData?: { name: string; description?: string; columns?: any[] }) => {
        try {
            clearToasts(); // Clear any existing toasts before starting
            if (!selectedDataObject) {
                throw new Error('No data object selected for file replacement');
            }

            // Use existing data object UUID directly
            const targetUuid = selectedDataObject.uuid;
            if (!targetUuid) throw new Error('Selected data object missing UUID');

            // Instead of using analyzeReplacedFile, call the async analysis directly to get the job ID
            const { reportService } = await import('../services/reportService');

            // Find folder information for this data object
            const currentFolder = selectedDataObject.folderId ? folders.find(f => f.id === selectedDataObject.folderId) : undefined;
            const folderInfo = currentFolder ? {
                folderId: currentFolder.id,
                folderUuid: currentFolder.uuid,
                folderName: currentFolder.name
            } : undefined;

            // Start async analysis — backend retrieves description from data_object_id
            const { jobId } = await reportService.startAsyncAnalysisFile(
                file,
                dataObjectData?.name || selectedDataObject.name,
                targetUuid,
                folderInfo,
                activeLanguage
            );

            // Analysis job started successfully — from this point, errors in UI
            // state management should NOT show a false "analysis failed" message.

            // Record activity for immediate responsive polling
            recordActivity();

            try {
                // Update data object status to processing
                await dataObjectService.updateDataSource(selectedDataObject.id, {
                    status: 'processing' as any,
                    dataObjectUuid: targetUuid
                });
            } catch {
                // Status update failed but analysis is running — continue silently
            }

            // Close the current modal
            setIsModalOpen(false);

            // Update local state to show processing status immediately for better UX
            const updatedDataSource = { ...selectedDataObject, status: 'processing' as const };
            setDataSources(prev => prev.map(ds =>
                ds.id === selectedDataObject.id ? updatedDataSource : ds
            ));

            // Open the actions modal with direct job ID for immediate polling
            const dataObjectWithJobId = {
                ...updatedDataSource,
                jobId: jobId,
                shouldCheckForJob: jobId
            } as DataSourceType;
            setActionsDataObject(dataObjectWithJobId);
            setShowActionsModal(true);

            // Note: Analysis start toast removed to avoid duplicates - progress shown in modal

            // Note: No immediate refresh to avoid page reload - status will be updated via polling
        } catch (error) {
            pushToast({
                variant: 'warning',
                message: `Analysis could not be completed at this time. Please try again later.`
            });
        }
    };


    if (isInitialLoad && loading) {
        return (
            <div className="enhanced-loading-container zoom-reset">
                <div className="loading-background-animation"></div>
                <div className="loading-content mt-12 sm:mt-24">
                    {/* Logo positioned just above the dialog box */}
                    <div className="flex justify-center mb-4 sm:mb-8 -mt-10 sm:-mt-20">
                        <img
                            src="/new_logo.png"
                            alt="Data Doctor Logo"
                            className="h-24 sm:h-40 w-auto object-contain opacity-90"
                            style={{
                                filter: 'drop-shadow(0 4px 12px rgba(155, 5, 39, 0.08))',
                                maskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%), radial-gradient(ellipse at center, black 85%, transparent 100%)',
                                WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%), radial-gradient(ellipse at center, black 85%, transparent 100%)',
                                maskComposite: 'intersect',
                                WebkitMaskComposite: 'source-in'
                            }}
                        />
                    </div>
                    <div className="loader-red loader-lg -mt-8"></div>
                    <div className="loading-text-primary">{t('Loading data objects...')}</div>
                    <div className="loading-text-secondary">{t('Please wait while we fetch your data')}</div>
                    <div className="loading-progress-bar">
                        <div className="progress-fill"></div>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="alert alert-error">
                <div className="layout-cluster">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-fluid-sm font-medium text-black">{t('Error Loading Data Objects')}</h3>
                        <div className="text-fluid-sm text-black">{error}</div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <PageLayout>
            <div className="space-y-4">
                <WelcomeBanner isFirstLogin={isFirstLogin} />
                <HeaderSection
                    isInFolderView={isInFolderView}
                    selectedFolder={selectedFolder}
                    onBackToMain={handleBackToMainView}
                    onShowCreateFolder={handleShowCreateFolder}
                    onAddDataObject={isInFolderView ? handleAddDataObjectToFolder : handleAddDataSource}
                    onShowFeedback={() => setShowFeedbackModal(true)}
                    disabled={buttonDisabled}
                    isSelecting={isSelecting}
                    selectedCount={selectedIds.length}
                    onToggleSelect={toggleSelection}
                    hasSelectableItems={isInFolderView ? filteredDataObjects.length > 0 : unassignedDataSources.length > 0}
                    onBulkMove={() => {
                        if (selectedIds.length > 0) {
                            // Create a mock data object for the modal (it just needs an ID for display)
                            setMoveTarget({
                                id: selectedIds[0],
                                name: `${selectedIds.length} selected items`,
                                uuid: 'bulk-move',
                                folderId: selectedFolder?.id // Set correct folderId for current location display
                            } as any);
                            setShowMoveModal(true);
                        }
                    }}
                    onBulkDelete={() => {
                        if (selectedIds.length > 0) {
                            handleBulkDelete()
                        }
                    }}
                    onSelectAll={selectAllVisible}
                    onClearSelection={clearSelection}
                />

                {isInFolderView && selectedFolder ? (
                    <FolderView
                        selectedFolder={selectedFolder}
                        dataObjects={filteredDataObjects}
                        onDeleteDataObject={handleDeleteDataSource}
                        onRemoveFromFolder={(id) => {
                            setDataSources(prev => prev.map(ds => ds.id === id ? { ...ds, folderId: undefined } : ds))
                        }}
                        onDataObjectClick={handleDataObjectClick}
                        onRefreshDataObject={handleRefreshDataObject}
                        isSelecting={isSelecting}
                        selectedIds={selectedIds}
                        onCardSelection={handleCardSelection}
                        onToggleSelect={() => setIsSelecting(!isSelecting)}
                        hasSelectableItems={filteredDataObjects.length > 0}
                        selectedCount={selectedIds.length}
                        onBulkMove={() => {
                            if (selectedIds.length > 0) {
                                // Create a mock data object for the modal (it just needs an ID for display)
                                setMoveTarget({
                                    id: selectedIds[0],
                                    name: `${selectedIds.length} selected items`,
                                    uuid: 'bulk-move',
                                    folderId: selectedFolder?.id // Set correct folderId for current location display
                                } as any);
                                setShowMoveModal(true);
                            }
                        }}
                        onBulkDelete={() => {
                            if (selectedIds.length > 0) {
                                handleBulkDelete()
                            }
                        }}
                        onSelectAll={selectAllVisible}
                        onClearSelection={clearSelection}
                    />
                ) : (
                    <MainView
                        folders={folders}
                        dataSources={dataSources}
                        showCreateFolder={false}
                        onCreateFolder={handleCreateFolder}
                        onCancelCreateFolder={() => setShowCreateFolder(false)}
                        onFolderClick={handleFolderClick}
                        onDeleteFolder={async (id) => {
                            try {
                                await deleteFolder(id)
                                pushToast({ variant: 'success', message: 'Folder deleted' })
                            } catch {
                                pushToast({ variant: 'error', message: 'Failed to delete folder' })
                            }
                        }}
                        onDataObjectClick={handleDataObjectClick}
                        onDeleteDataObject={handleDeleteDataSource}
                        onShowCreateFolder={handleShowCreateFolder}
                        onAddDataSource={handleAddDataSource}
                        onRefreshDataObject={handleRefreshDataObject}
                        disabled={buttonDisabled}
                        isSelecting={isSelecting}
                        selectedIds={selectedIds}
                        onCardSelection={handleCardSelection}
                        onToggleSelect={() => setIsSelecting(!isSelecting)}
                        hasSelectableItems={dataSources.filter(ds => !ds.folderId).length > 0}
                        selectedCount={selectedIds.length}
                        onBulkMove={() => {
                            if (selectedIds.length > 0) {
                                // Create a mock data object for the modal (it just needs an ID for display)
                                setMoveTarget({
                                    id: selectedIds[0],
                                    name: `${selectedIds.length} selected items`,
                                    uuid: 'bulk-move',
                                    folderId: selectedFolder?.id // Set correct folderId for current location display
                                } as any);
                                setShowMoveModal(true);
                            }
                        }}
                        onBulkDelete={handleBulkDelete}
                        onSelectAll={selectAllVisible}
                        onClearSelection={clearSelection}
                    />
                )}

                <AddDataSourceModal
                    isOpen={showAddDataSourceModal}
                    onClose={() => setShowAddDataSourceModal(false)}
                    onUploadFile={handleFileUploadWithAnalysis}
                    title={isInFolderView ? t("Data Object Information") : t("Add Data Object")}
                    pendingUploadFolder={pendingUploadFolder}
                    onDatabricksAnalysisComplete={async (dataSource, columns, tableName) => {
                        try {
                            // Handle folder view switching like file uploads do
                            if (pendingUploadFolder && !isInFolderView) {
                                setSelectedFolder(pendingUploadFolder);
                                setIsInFolderView(true);
                                // Update URL to preserve folder view
                                setSearchParams({ folderId: pendingUploadFolder.id.toString() });
                            }
                            setPendingUploadFolder(null);

                            // Create deferred DQ rules for Databricks tables (same as file uploads)
                            if (dataSource.uuid && columns && columns.length > 0) {
                                try {
                                    await dataObjectService.createDeferredRules(dataSource.uuid, columns, tableName);
                                } catch (ruleError) {
                                    console.error('Failed to create DQ rules for Databricks table:', ruleError);
                                    // Don't fail the entire flow if rule creation fails
                                    pushToast({
                                        variant: 'warning',
                                        message: 'Table created successfully, but some DQ rules could not be created.'
                                    });
                                }
                            }

                            // Refresh data to get updated status and show processing card in correct location
                            await refreshData();

                            // Show success message but don't navigate immediately
                            // Let user see the processing card in the correct folder/main view
                            pushToast({
                                variant: 'success',
                                message: `${dataSource.name} created successfully! Analysis is starting...`
                            });

                            // Close the modal so user can see the processing card
                            setShowAddDataSourceModal(false);

                        } catch (error) {
                            console.error('Failed to complete Databricks analysis setup:', error);
                            pushToast({
                                variant: 'error',
                                message: `Failed to start analysis for ${dataSource.name}. Please try again.`
                            });
                        }
                    }}
                />
                <CreateFolderModal
                    isOpen={showCreateFolder}
                    onClose={() => {
                        setShowCreateFolder(false)
                        if (!isModalOpen && !isInAddDataObjectFlowRef.current) setPendingUploadFolder(null)
                    }}
                    onCreate={handleCreateFolder}
                    availableDataSources={dataSources}
                    folders={folders}
                    onAssignDataSources={assignDataSourcesToFolder}
                    onCreateAndAddNewDataObject={handleCreateAndAddNewDataObject}
                    existingFolderNames={folders.map(f => f.name)}
                />
                <FileUploadModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        isInAddDataObjectFlowRef.current = false
                        if (previousModal === 'actions') setShowActionsModal(true)
                        else if (previousModal === 'addDataSource') setShowAddDataSourceModal(true)
                        setPreviousModal(null)
                        setPendingUploadFolder(null)
                    }}
                    onUpload={(file, dataObjectData) => (
                        modalMode === 'replace'
                            ? handleFileReplaceWithAnalysis(file, dataObjectData)
                            : handleFileUploadWithAnalysis(file, dataObjectData)
                    )}
                    mode={modalMode}
                    selectedDataObject={selectedDataObject}
                />
                <DataObjectActionsModal
                    isOpen={showActionsModal}
                    dataObject={actionsDataObject}
                    onClose={() => {
                        setShowActionsModal(false)
                        setActionsDataObject(undefined)
                    }}
                    folders={folders}
                    onMoveRequest={(dataObject) => {
                        setMoveTarget(dataObject)
                        setShowActionsModal(false)
                        setShowMoveModal(true)
                    }}
                    onViewReport={async () => {
                        if (!actionsDataObject) return;
                        clearToasts(); // Clear any existing toasts before navigation

                        try {
                            // First, try to get the latest historical report for this data object
                            const { authenticatedHistoricalReportService } = await import('../services/historicalReportService.backend');

                            const reports = await authenticatedHistoricalReportService.listReports({
                                data_object_id: actionsDataObject.uuid,
                                order_by: 'generated_at',
                                order_direction: 'desc',
                                limit: 1,
                                status: 'analyzed' // Use valid status value
                            });

                            if (reports.reports && reports.reports.length > 0) {
                                // Build return URL with folder context
                                const returnUrl = isInFolderView && selectedFolder
                                    ? `/datasources?folderId=${selectedFolder.id}`
                                    : '/datasources';

                                // Navigate to the latest historical report
                                const latestReport = reports.reports[0];
                                navigate(`/report?reportId=${encodeURIComponent(latestReport.id)}&returnUrl=${encodeURIComponent(returnUrl)}`);
                            } else {
                                // Build return URL with folder context
                                const returnUrl = isInFolderView && selectedFolder
                                    ? `/datasources?folderId=${selectedFolder.id}`
                                    : '/datasources';

                                // Fallback to live analysis if no historical reports found
                                navigate('/report', {
                                    state: {
                                        dataObjectName: actionsDataObject?.name,
                                        dataObjectUuid: actionsDataObject?.uuid,
                                        // Keep legacy ID for fallback compatibility
                                        dataObjectId: actionsDataObject?.id,
                                        returnUrl: returnUrl
                                    }
                                });
                            }
                        } catch (error) {
                            // Build return URL with folder context
                            const returnUrl = isInFolderView && selectedFolder
                                ? `/datasources?folderId=${selectedFolder.id}`
                                : '/datasources';

                            // Fallback to live analysis on error
                            navigate('/report', {
                                state: {
                                    dataObjectName: actionsDataObject?.name,
                                    dataObjectUuid: actionsDataObject?.uuid,
                                    // Keep legacy ID for fallback compatibility
                                    dataObjectId: actionsDataObject?.id,
                                    returnUrl: returnUrl
                                }
                            });
                        }
                    }}
                    onUpdateMetadata={async (id, updates) => {
                        try {
                            // Update the backend first
                            await dataObjectService.updateDataSource(id, updates);

                            // Update local state - only change the fields the user actually updated.
                            // Do NOT blindly spread the backend response, as its UUID-derived numeric IDs
                            // or folderId may differ from the bootstrap-loaded values and cause the item to vanish.
                            setDataSources(prev => {
                                const updated = prev.map(ds => {
                                    if (ds.id === id) {
                                        const result = {
                                            ...ds, // Keep ALL original data (id, uuid, folderId, type, etc.)
                                            // Only update fields that the user actually changed
                                            ...(updates.name ? { name: updates.name } : {}),
                                            ...(updates.description !== undefined ? { description: updates.description } : {}),
                                        };
                                        return result;
                                    }
                                    return ds;
                                });
                                return updated;
                            });

                            const nameForToast = updates.name || dataSources.find(d => d.id === id)?.name || 'data object';
                            pushToast({ variant: 'success', message: `Updated ${nameForToast} metadata` })
                        } catch (error) {
                            console.error('[DataSource] Failed to update metadata:', error);
                            pushToast({ variant: 'error', message: 'Failed to update metadata. Please try again.' })
                        }
                    }}
                    onDataSourceUpdated={(updatedDataSource) => {
                        // Update local state immediately with the provided data source
                        setDataSources(prev => prev.map(ds =>
                            ds.id === updatedDataSource.id ? updatedDataSource : ds
                        ))
                    }}
                    onUploadFile={(dataObject) => {
                        // Upload new file to replace existing data object (no auto-analysis)
                        setModalMode('replace')
                        setSelectedDataObject(dataObject)
                        setPreviousModal('actions')
                        setShowActionsModal(false)
                        setIsModalOpen(true)
                    }}
                    runningJobs={runningJobs}
                    getJobForDataObject={getJobForDataObject}
                    addWatchedObject={addWatchedObject}
                    removeWatchedObject={removeWatchedObject}
                    getPersistedProgressForObject={getPersistedProgressForObject}
                    onFastRefresh={handleFastRefresh}
                />
                <MoveDataObjectModal
                    isOpen={showMoveModal}
                    dataObject={moveTarget}
                    folders={folders}
                    onCreateFolder={handleCreateFolder}
                    onClose={() => {
                        setShowMoveModal(false)
                        // Only restore actions modal for single item moves, not bulk moves
                        if (moveTarget && moveTarget.uuid !== 'bulk-move') {
                            setActionsDataObject(moveTarget)
                            setShowActionsModal(true)
                        }
                        setMoveTarget(undefined)
                    }}
                    onMove={async (targetFolderId, createdFolder) => {
                        // Handle bulk move if this is a bulk operation
                        if (moveTarget && moveTarget.uuid === 'bulk-move') {
                            await handleBulkMove(targetFolderId, createdFolder);
                            setShowMoveModal(false);
                            setMoveTarget(undefined);
                            return;
                        }

                        // Handle single item move (existing logic)
                        if (!moveTarget) return;

                        try {
                            // Use created folder if provided, otherwise find in existing folders state
                            const targetFolder = createdFolder || (targetFolderId ? folders.find(f => f.id === targetFolderId) : null);
                            const folderUuid = targetFolder?.uuid; // Get the actual folder UUID

                            // Create update object with proper typing
                            const updateData: any = {
                                folderId: targetFolderId,
                                dataObjectUuid: moveTarget.uuid // Pass the data object UUID for proper identification
                            };

                            // When moving to root (unassign), use real root folder UUID if available
                            if (!targetFolderId && rootFolderUUID) {
                                updateData.folderUuid = rootFolderUUID;
                            } else if (folderUuid) {
                                updateData.folderUuid = folderUuid;
                            }

                            // Call backend API to update the data object's folder assignment
                            await dataObjectService.updateDataSource(moveTarget.id, updateData);

                            // Update local state after successful backend update
                            setDataSources(prev => prev.map(ds =>
                                ds.id === moveTarget.id ? { ...ds, folderId: targetFolderId } : ds
                            ));

                            const destination = targetFolderId ? folders.find(f => f.id === targetFolderId)?.name : 'Unassigned';
                            pushToast({ variant: 'success', message: `Moved ${moveTarget.name} to ${destination}` });

                            const updated = { ...moveTarget, folderId: targetFolderId };
                            setActionsDataObject(updated);
                            setShowActionsModal(true);

                        } catch (error) {
                            console.error('[DataSource] Move failed', error);
                            pushToast({
                                variant: 'error',
                                message: `Failed to move ${moveTarget.name}. Please try again.`
                            });
                        } finally {
                            setShowMoveModal(false);
                            setMoveTarget(undefined);
                        }
                    }}
                />
            </div>

            {/* Delete Confirmation Modal - Simplified */}
            <div style={{ display: showDeleteConfirmation ? 'flex' : 'none' }} className="fixed inset-0 bg-black bg-opacity-50 items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
                    <p className="text-gray-700 mb-6">
                        Are you sure you want to delete {deleteTarget?.ids.length || 0} item(s)?
                        This action cannot be undone and you may lose important data.
                    </p>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setShowDeleteConfirmation(false)
                                setDeleteTarget(null)
                            }}
                            className={getButtonClasses('secondary')}
                            style={{
                                ...getButtonStyles('secondary'),
                                backgroundColor: '#6B7280', // Gray for cancel
                                color: 'white'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={executeDelete}
                            className={getButtonClasses('primary')}
                            style={getButtonStyles('primary')}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* Floating Action Buttons - bottom right */}
            <div className="fixed bottom-6 right-6 z-40 flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => setShowLanguageModal(true)}
                    className="inline-flex items-center justify-center w-12 h-12 text-white rounded-full shadow-lg transition-all duration-200 flex-shrink-0"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.015)';
                        e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                    }}
                    aria-label="Change Language"
                >
                    <IoLanguage className="w-6 h-6" />
                </button>

                <button
                    type="button"
                    onClick={() => setShowFeedbackModal(true)}
                    className="inline-flex items-center justify-center px-5 py-3 text-white font-semibold rounded-full shadow-lg transition-all duration-200 notranslate whitespace-nowrap"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.015)';
                        e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                    }}
                    aria-label="Send Feedback"
                >
                    <VscFeedback className="mr-2 w-5 h-5" />
                    {t('Feedback')}
                </button>
            </div>

            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                dataSources={dataSources}
                historicalReports={historicalReports}
            />

            {/* Language Modal */}
            <LanguageSelectorModal isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)} />
        </PageLayout>
    );
}

export default DataSource;